import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Plus, Search, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/auth-client";
import { rowActivation } from "@/lib/row-navigation";
import type { VendorDto, VendorStatus } from "@procureflow/shared-types";
import { ExportCsvButton } from "@/components/export-csv-button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/vendors")({
  head: () => ({
    meta: [
      { title: "Vendors — ProcureFlow" },
      {
        name: "description",
        content: "Vendor master list — search, filter by status, and create new vendor records.",
      },
    ],
  }),
  // Mirrors purchase-requests.tsx: show the list at /vendors, otherwise
  // pass through to the matched child (vendors.new / vendors.$id).
  component: function VendorsPageOrOutlet() {
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <VendorsList />;
  },
});

const STATUS_FILTERS: Array<{ value: "" | VendorStatus; label: string }> = [
  { value: "", label: "All" },
  { value: "PendingApproval", label: "Pending Approval" },
  { value: "Active", label: "Active" },
  { value: "Rejected", label: "Rejected" },
];

function statusBadgeClasses(status: VendorStatus): string {
  switch (status) {
    case "Active":
      return "[color:var(--success)] bg-success/10 border-success/20";
    case "PendingApproval":
      return "[color:var(--warning)] bg-warning/10 border-warning/20";
    case "Rejected":
      return "text-destructive bg-destructive/10 border-destructive/20";
  }
}

function statusLabel(status: VendorStatus): string {
  return status === "PendingApproval" ? "Pending Approval" : status;
}

function VendorsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = user?.role === "ProcurementOfficer" || user?.role === "Admin";

  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | VendorStatus>("");

  useEffect(() => {
    let cancelled = false;
    async function fetchList() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ limit: "100" });
        if (search) params.set("search", search);
        if (statusFilter) params.set("status", statusFilter);
        const res = await fetchWithAuth(`/api/vendors?${params.toString()}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setError(body.message ?? "Failed to load vendors.");
          return;
        }
        const { data } = (await res.json()) as { data: VendorDto[] };
        if (!cancelled) setVendors(data);
      } catch {
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchList();
    return () => {
      cancelled = true;
    };
  }, [search, statusFilter]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Vendors</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Master list of vendors — search by name, filter by approval status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportCsvButton
            endpoint="/api/vendors/export/csv"
            searchParams={(() => {
              const p = new URLSearchParams();
              if (search) p.set("search", search);
              if (statusFilter) p.set("status", statusFilter);
              const qs = p.toString();
              return qs.length > 0 ? qs : undefined;
            })()}
            fallbackFilename="vendors.csv"
          />
          {canCreate && (
            <Link
              to="/vendors/new"
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-glow hover:opacity-95"
            >
              <Plus className="h-4 w-4" /> Create Vendor
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 flex-1 min-w-[240px] max-w-md">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company name"
            aria-label="Search vendors"
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
        <select
          aria-label="Status filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm"
        >
          {STATUS_FILTERS.map((opt) => (
            <option key={opt.label} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : vendors.length === 0 ? (
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          <Building2 className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm font-medium">No vendors match the current filters.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Tax ID</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendors.map((v) => (
                <TableRow
                  key={v.id}
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  {...rowActivation(() => navigate({ to: "/vendors/$id", params: { id: v.id } }))}
                >
                  <TableCell>
                    <Link
                      to="/vendors/$id"
                      params={{ id: v.id }}
                      className="font-medium text-primary hover:underline"
                    >
                      {v.company_name}
                    </Link>
                    <p className="text-xs text-muted-foreground mt-0.5">{v.address}</p>
                  </TableCell>
                  <TableCell>
                    <p>{v.contact_name}</p>
                    <p className="text-xs text-muted-foreground">{v.contact_email}</p>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{v.tax_id}</TableCell>
                  <TableCell className="font-mono text-xs">{v.bank_details}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClasses(v.status)}`}
                    >
                      {statusLabel(v.status)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
